import { createStyles } from 'antd-style';

import {
  ThemeBorderRadius,
  ThemeColors,
  ThemeFont,
  ThemeSpaces,
} from '../../theme';
import { FA_SELECTOR } from '../../styles/variables';
import {
  absolute,
  absoluteMiddleNextToRight,
  flexCenter,
} from '../../styles/mixins';
import {
  menuItemActive,
  menuItemHover,
  menuItemPressed,
} from '../../styles/dropdownMixins';

// @MonitorLeftColumn (320) - @spaceXs (4) + 9 — aligns with the scrollbar outside
const CURRENT_STATE_WIDTH = 325;
// @spaceNorm5 (50) - @spaceXs (4)
const CURRENT_STATE_PADDING_LEFT = 46;

export const useMonitorSearchFiltersStyles = createStyles(
  ({ token, css, cx }) => {
    const popupContainer = cx(
      css({
        position: 'relative',

        '> .ant-popover-open': {
          position: 'relative',
          zIndex: 1,
        },
      })
    );

    const dateRangeFilterOverlay = cx(css`
      && {
        width: 360px;

        .ant-popover-inner-content {
          padding: ${ThemeSpaces.SPACE_NORM_15}px ${ThemeSpaces.SPACE_NORM_15}px
            ${ThemeSpaces.SPACE_NORM}px ${ThemeSpaces.SPACE_NORM_15}px;
        }
      }
    `);

    return {
      popupContainer,
      dateRangeFilterOverlay,
      dateRangeFilterContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',

        [`.${popupContainer}`]: {
          [`.${dateRangeFilterOverlay} > .ant-popover-arrow`]: {
            visibility: 'hidden',
          },

          '.DateRangePicker__CalendarPopup': {
            '.DateRangePicker__InputLike': {
              height: 28,

              '> .fa-calendar, > .DateRangePicker__DateInput': {
                fontSize: 18,
              },
            },

            '.DateRangePicker__Calendar__MonthSelector': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: `${ThemeSpaces.SPACE_XXS}px 0 ${ThemeSpaces.SPACE_XS}px 0`,

              '.ant-select': {
                fontSize: token.fontSizeLG,
                fontWeight: 500,
                color: ThemeColors.COLOR_GREY_DARK5,
                width: 'fit-content',

                '.ant-select-arrow': {
                  color: ThemeColors.COLOR_GREY6,
                },
              },
            },
          },
        },
      },
      root: {
        flexWrap: 'initial',

        [`@media (min-width: ${token.screenMDMin}px) and (max-width: ${token.screenMDMax}px)`]:
          {
            marginTop: ThemeSpaces.SPACE_NORM,
            marginBottom: ThemeSpaces.SPACE_NORM,
          },
        [`@media (max-width: ${token.screenSMMax}px)`]: {
          marginTop: ThemeSpaces.SPACE_NORM0_5,
          marginBottom: ThemeSpaces.SPACE_NORM0_5,
        },

        '.ConfigurableCard-Header .IconButton': {
          cursor: 'pointer',

          [FA_SELECTOR]: {
            fontSize: 16,
            color: ThemeColors.COLOR_GREY8_1,
          },

          '&.active': {
            [FA_SELECTOR]: {
              color: token.colorWhite,
            },
          },
        },

        '.ConfigurableCard-Body': {
          backgroundImage: `linear-gradient(180deg, ${token.colorWhite} 0%, ${ThemeColors.COLOR_GREY9_9} 100%)`,
          width: '100%',
        },
      },
      column: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',

        [`@media (min-width: ${token.screenLGMin}px)`]: {
          height: 40,
          padding: `0 ${ThemeSpaces.SPACE_NORM}px`,

          '&:not(:nth-child(4))': {
            borderRight: `1px solid ${ThemeColors.COLOR_GREY7_8}`,
          },

          '&:first-child': {
            width: 225,
          },
          '&:nth-child(2)': {
            width: 300,
          },
          '&:last-child': {
            width: 90,
          },
        },
        [`@media (max-width: ${token.screenMDMax}px)`]: {
          '&:nth-child(2)': {
            width: 'calc(100% - 160px)',
          },
          width: 125,
        },
      },
      popup: {
        paddingTop: 18,

        [`@media (max-width: ${token.screenMDMax}px)`]: {
          minWidth: 250,
        },

        '.ant-popover-inner-content': {
          padding: 0,
        },
      },
      // Overlay modifier composed with `popup` on the Stage filter popover.
      stageFilterOverlay: {
        '&&': {
          paddingTop: ThemeSpaces.SPACE_NORM_20,
        },
      },
      stageFilterLocationModeHeader: {
        padding: '6px 12px',
        borderBottom: `1px solid ${ThemeColors.COLOR_GREY7_8}`,

        '.ant-radio-group': {
          display: 'flex',
          justifyContent: 'flex-start',

          '.ant-radio-wrapper': {
            flexDirection: 'row',
            color: ThemeColors.COLOR_GREY_BLUISH,

            '&.ant-radio-wrapper-checked': {
              color: ThemeColors.COLOR_GREY3,
            },

            '.ant-radio': {
              marginRight: ThemeSpaces.SPACE_XS,
            },
          },
        },
      },
      popupLabel: {
        '&&': {
          ...flexCenter(),
          color: token.colorPrimary,
          fontWeight: 'bold',
          borderRadius: token.borderRadius,
          height: 30,

          [`@media (max-width: ${token.screenMDMax}px)`]: {
            justifyContent: 'flex-start',

            '&.ant-popover-open': {
              justifyContent: 'center',
            },
          },
          [`@media (min-width: ${token.screenMDMin}px) and (max-width: ${token.screenMDMax}px)`]:
            {
              padding: `0 ${ThemeSpaces.SPACE_SM}px`,
            },
          [`@media (max-width: ${token.screenSMMax}px)`]: {
            padding: 0,
          },

          '.DatePreviewIcon': {
            color: token.colorPrimary,
          },

          [FA_SELECTOR]: {
            marginLeft: ThemeSpaces.SPACE_SM,
            [`@media (max-width: ${token.screenSMMax}px)`]: {
              marginLeft: ThemeSpaces.SPACE_XS,
            },
          },
        },
      },
      sortIcon: {
        ...absolute(0),
        left: 'initial',
        width: 30,
        ...flexCenter(),
        color: ThemeColors.COLOR_GREY3,

        '&:hover': {
          color: ThemeColors.COLOR_RED6,
        },

        '.IconButton-Icon': {
          fontSize: 12,
          transform: 'rotateZ(90deg)',
        },

        '&.active': {
          color: token.colorPrimary,
          boxShadow: `inset 0 3px 6px ${ThemeColors.COLOR_GREY7_8}`,
          borderLeft: `1px solid ${ThemeColors.COLOR_GREY7_8}`,

          '.IconButton-Icon': {
            fontSize: 20,
            transform: 'none',
          },
        },

        [`@media (max-width: ${token.screenMDMax}px)`]: {
          display: 'none',
        },
      },
      footer: {
        height: 40,
      },
      currentState: {
        display: 'flex',
        alignItems: 'center',
        width: CURRENT_STATE_WIDTH,
        paddingLeft: CURRENT_STATE_PADDING_LEFT,
        fontWeight: 500,
        fontStyle: 'italic',
        fontSize: ThemeFont.FONT_SIZE_SM_PLUS,
        color: ThemeColors.COLOR_GREY3,
        lineHeight: '15px',

        '.MonitorSearchFilters-CurrentStateBadge': {
          marginRight: ThemeSpaces.SPACE_XS,
          padding: `${ThemeSpaces.SPACE_XXS}px ${ThemeSpaces.SPACE_SM}px`,
          lineHeight: 1,
          minWidth: 24,
          backgroundColor: ThemeColors.COLOR_GREY_BLUISH,
          borderRadius: ThemeBorderRadius.BORDER_RADIUS_MD,
          fontWeight: 'bold',
          color: token.colorWhite,
        },
      },
      tags: {
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        height: 30,

        '.ant-tag': {
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          height: 21,
          lineHeight: '19px',
          maxWidth: 200,
          padding: `0 ${ThemeSpaces.SPACE_NORM}px`,
          borderRadius: 12,
          border: `1px solid ${ThemeColors.COLOR_GREY_BLUISH}`,
          fontWeight: 500,
          backgroundColor: ThemeColors.COLOR_GREY_BLUISH,
          color: token.colorWhite,

          [`> * ${FA_SELECTOR}`]: {
            fontSize: token.fontSize,
            color: 'inherit',
            marginLeft: -ThemeSpaces.SPACE_XXS,
            marginRight: ThemeSpaces.SPACE_XS,
          },
          '> .text': {
            flex: 1,
            minWidth: 0,
            minHeight: 0,
          },
          '> *': {
            display: 'block',
          },
          '&, > .text': {
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          },

          '&.closable': {
            paddingRight: ThemeSpaces.SPACE_NORM_30,
          },
          '&.changed': {
            border: `1px solid ${ThemeColors.COLOR_RED}`,
            color: token.colorPrimary,
            backgroundColor: token.colorWhite,
          },
          '&.deleted': {
            border: `1px solid ${ThemeColors.COLOR_RED}`,
            color: token.colorPrimary,
            backgroundColor: 'transparent',
          },
          '&.ant-tag-hidden': {
            display: 'none',
          },
          '.ant-tag-close-icon': {
            ...absoluteMiddleNextToRight(),
            right: ThemeSpaces.SPACE_SM,
            fontSize: 13,
            color: 'inherit',
          },
        },
      },
      buttons: {
        ...flexCenter(),
        paddingRight: ThemeSpaces.SPACE_NORM,

        '.InlineLink': {
          color: token.colorPrimary,
          fontSize: ThemeFont.FONT_SIZE_SM_MINUS,
          fontWeight: 500,
          marginRight: ThemeSpaces.SPACE_NORM_15,

          // Firefox renders in a buggy way if a parent has underline and a child contains text
          '> *': {
            textDecoration: 'underline',
          },

          '&.disabled': {
            color: ThemeColors.COLOR_GREY3_5,
            opacity: 0.25,
          },
        },
      },
      headerDivider: {
        height: 14,
        width: 2,
        backgroundColor: token.colorWhite,
        opacity: 0.5,
        marginLeft: ThemeSpaces.SPACE_NORM0_5,
        marginRight: ThemeSpaces.SPACE_NORM,
      },
      paginationText: {
        color: ThemeColors.COLOR_GREY6,
        fontSize: ThemeFont.FONT_SIZE_SM_MINUS,
        fontWeight: 500,
        fontStyle: 'italic',

        '> strong': {
          color: token.colorPrimary,
        },
      },
      searchSingleSelectGroup: {
        padding: `0 ${ThemeSpaces.SPACE_NORM_15}px`,
      },
      stageLabelText: {
        flex: 1,
      },
      stageLabelCount: {
        marginLeft: 4,
      },
      searchSingleSelectItem: {
        cursor: 'pointer',
        padding: `${ThemeSpaces.SPACE_NORM0_5}px ${ThemeSpaces.SPACE_NORM_15}px`,
        lineHeight: '18px',
        color: ThemeColors.COLOR_GREY6,
        fontWeight: 500,

        '&:not(.active):not(:hover):not(:active):nth-child(odd)': {
          backgroundColor: '#e5ebf0',
        },

        '&.active': menuItemActive,
        '&:hover': menuItemHover,
        '&:active': menuItemPressed,
      },
      statusCheckboxListItem: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        fontWeight: 400,

        '.ant-checkbox': {
          marginRight: ThemeSpaces.SPACE_NORM0_5,
        },
      },
      statusCheckboxListLabel: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      },
      statusMultiSelect: {
        '.ant-select-selector': {
          height: 40,
          overflow: 'hidden',

          '&:after': {
            margin: 0,
          },
        },

        '.ant-select-selection-wrap::after': {
          content: 'none',
        },

        '.ant-select-selection-overflow-item-suffix': {
          display: 'none',
        },

        '.ant-select-selection-overflow': {
          width: '100%',
        },
      },
      statusMultiSelectSummary: {
        display: 'inline',
      },
      searchTextFilterSubmenu: {
        position: 'relative',
        borderRadius: token.borderRadius,
        overflow: 'hidden',
      },
      searchTextFilterSubmenuEntering: {
        ...absolute(0),
        backgroundColor: ThemeColors.COLOR_WHITE6,
        padding: ThemeSpaces.SPACE_NORM_20,
        paddingTop: ThemeSpaces.SPACE_NORM,

        '> *': {
          marginBottom: ThemeSpaces.SPACE_NORM,
          '&:last-child': {
            marginBottom: 0,
          },
        },

        '.InlineLink': {
          color: token.colorPrimary,
          fontWeight: 'bold',
        },
      },
    };
  }
);
